<script setup lang="ts">
import { computed, onBeforeMount, ref } from 'vue';
import { getCartContent } from '@/services/api';
import useBookingStore from '@/store/booking';
import useFormProductStore from '@/store/form-product';
import useInitStore from '@/store/init';
import useOrderStore from '@/store/order';
import type { SelectedArticle } from '@/types/common';
import ActionCode from './action-code.vue';
import Cart from './product-cart.vue';
import ProductConfiguration from './product-configuration.vue';
import type { ArticleWithActionCode } from '../../../../types/action-code';

const bookingStore = useBookingStore();
const formProductStore = useFormProductStore();
const initStore = useInitStore();

const configuration = ref();
const productConfigurationRef = ref();
const chartReloading = ref<boolean>(false);

async function updateConfiguration() {
  chartReloading.value = true;

  const selectedArticles = formProductStore.selections.map(({ article }) => article);

  const [contentError, content] = await getCartContent(selectedArticles);

  if (!contentError) {
    configuration.value = content;
  }
  chartReloading.value = false;
}

async function isValid() {
  const { valid } = await productConfigurationRef.value.validate();

  return valid;
}

const showActionCode = computed<boolean>(() => (configuration.value
  ? configuration.value.cartItems.some(
    (article: SelectedArticle) => article.name === 'Rechnung per Post' || article.name === 'Online-Rechnung',
  )
  : false));

async function removeActionCode(): Promise<void> {
  formProductStore.clearDiscounts();
  await updateConfiguration();
}

async function applyActionCode(actionCode: string, discountArticles: ArticleWithActionCode[])
: Promise<void> {
  formProductStore.applyActionCode(actionCode, discountArticles);
  await updateConfiguration();
}

onBeforeMount(async () => {
  if (!bookingStore.articles.length) {
    await bookingStore.loadArticles();
  }
});

defineExpose({
  applyData: () => {
    useOrderStore().addProductData(formProductStore.$state);
  },
  isValid,
});
</script>

<template>
  <v-form ref="productConfigurationRef" @submit.prevent>
    <v-row no-gutters>
      <v-col cols="12" md="8">
        <product-configuration
          :articles="bookingStore.articles"
          :selections-prop="formProductStore.selections"
          @update-configuration="updateConfiguration"
        />
      </v-col>

      <v-col cols="12" md="4" class="card-column">
        <div v-if="chartReloading" class="spinner-background">
          <v-progress-circular color="primary" indeterminate />
        </div>

        <cart
          v-else
          :articles="bookingStore.articles"
          :configuration="configuration"
          :chart-reloading="chartReloading"
          :price-list="initStore.priceList!"
          :only-product="bookingStore.isCurrentBookingModeAnyProductOnly"
        />

        <action-code
          v-if="showActionCode"
          v-show="!chartReloading"
          :initial-action-code="formProductStore.appliedActionCode"
          :micromarket-id="initStore.bookingApproach!.micromarketId.toString()"
          @apply-action-code="applyActionCode"
          @remove-action-code="removeActionCode"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<style scoped>
  .card-column {
    position: relative;
  }
  .spinner-background {
    position: absolute;
    width: calc(100% - 40px);
    height: calc(90% - 40px);
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>
